import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth, db } from '../services/firebase';
import { UserProfile, UserRole } from '../types';
import { deductUserCredit, refundUserCredit } from '../services/creditService';

interface AuthContextType {
  user: any | null; 
  profile: UserProfile | null;
  loading: boolean;
  role: UserRole;
  isAdmin: boolean;
  logout: () => Promise<void>;
  checkAndDeductCredit: (cost?: number, action?: string, note?: string) => Promise<boolean>;
  refundCredit: (amount?: number) => Promise<void>;
  error?: string; // Added to expose init errors
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  profile: null,
  loading: true,
  role: 'viewer',
  isAdmin: false,
  logout: async () => {},
  checkAndDeductCredit: async () => false,
  refundCredit: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<any | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (!auth) {
        console.warn("Auth service not initialized. Check Firebase config.");
        setError("Không thể kết nối đến máy chủ xác thực (Firebase Error).");
        setLoading(false);
        return;
    }

    const unsubscribeAuth = auth.onAuthStateChanged((currentUser: any) => {
      setLoading(true);
      if (currentUser) {
        if (!db) {
            console.error("Firestore not initialized");
            setUser(currentUser);
            setLoading(false);
            return;
        }

        const userDocRef = db.collection('users').doc(currentUser.uid);
        
        const unsubscribeSnapshot = userDocRef.onSnapshot((docSnap: any) => {
            let userProfile: UserProfile;

            if (docSnap.exists) {
                const data = docSnap.data();
                // Round credits to 1 decimal place to avoid 0.99999 issues
                const rawCredits = data.credits || 0;
                const roundedCredits = Math.round(rawCredits * 10) / 10;

                userProfile = {
                    ...data,
                    credits: roundedCredits
                } as UserProfile;
                
                if (userProfile.status === 'banned') {
                    alert("Tài khoản của bạn đang chờ duyệt hoặc đã bị khóa. Vui lòng liên hệ Admin.");
                    auth.signOut();
                    return;
                }

                if (!userProfile.storageTier) userProfile.storageTier = 'basic';
            } else {
                // Initialize new user as 'active' so they can use the app immediately
                const newProfile: UserProfile = {
                    uid: currentUser.uid,
                    email: currentUser.email || '',
                    displayName: currentUser.displayName || 'SEO Master',
                    photoURL: currentUser.photoURL || '',
                    role: 'editor',
                    credits: 5,
                    storageTier: 'basic',
                    status: 'active', // Changed from 'banned' to 'active'
                    createdAt: new Date().toISOString(),
                    lastLogin: new Date().toISOString()
                };
                
                userDocRef.set(newProfile, { merge: true }).catch((err: any) => {
                    console.error("Error creating user profile:", err);
                });
                userProfile = newProfile;
            }
            
            setUser(currentUser);
            setProfile(userProfile);
            setLoading(false);
        }, (error: any) => {
            console.error("Auth Snapshot Error:", error);
            setUser(currentUser);
            setProfile({
                uid: currentUser.uid,
                email: currentUser.email || '',
                displayName: currentUser.displayName || 'User',
                photoURL: currentUser.photoURL || '',
                role: 'viewer',
                credits: 0,
                storageTier: 'basic',
                status: 'banned',
                createdAt: new Date().toISOString(),
                lastLogin: new Date().toISOString()
            });
            setLoading(false);
        });

        return () => unsubscribeSnapshot();
      } else {
        setUser(null);
        setProfile(null);
        setLoading(false);
      }
    });

    return () => unsubscribeAuth();
  }, []);

  const logout = async () => {
    if (auth) await auth.signOut();
  };

  const checkAndDeductCredit = async (cost: number = 1, action: string = 'General AI Task', note: string = ''): Promise<boolean> => {
      if (!profile) return false;
      if (!db) return false;
      
      if (profile.credits < cost) {
          alert(`Bạn không đủ tín dụng! (Cần: ${cost}, Có: ${profile.credits}). Vui lòng nạp thêm.`);
          return false;
      }
      
      const success = await deductUserCredit(profile.uid, cost, action, note);
      if (!success) return false;
      return success;
  };

  const refundCredit = async (amount: number = 1) => {
      if (!profile || !db) return;
      await refundUserCredit(profile.uid, amount);
  };

  const role = profile?.role || 'viewer';
  const isAdmin = role === 'admin';

  // If there's a critical init error, we can render a fallback here or pass it down
  // For now, let the app render, but Login component might fail if it tries to use auth directly.
  
  return (
    <AuthContext.Provider value={{ user, profile, loading, role, isAdmin, logout, checkAndDeductCredit, refundCredit, error }}>
      {children}
    </AuthContext.Provider>
  );
};